import {IRoutesProvider} from 'Shared/Routes';
import { EventsGate, MenusGate, SpecialsGate, StaffResourcesGate } from 'Shared/backend/features/FeatureGates';
import { lazy } from 'solid-js';
import { sharedRoutes } from 'Shared/routes/SharedRoutes';


export class VenueRoutes implements IRoutesProvider
{
	routes()
	{
//TODO add redirect route for all trailing /s here. Currently a hack is fixed in Server.resolveRoute()

		return ([
			...this.frontend(),
			...this.backend(),
		]);
	}

	frontend()
	{
		return ([
			{
				path: '/staffResources',
				component: lazy(() => import('Shared/frontend/staffResources/StaffResourcePage')),
			},
			{
				path: '/',
				component: lazy(() => import('Shared/frontend/layouts/StandardLayout')),
				children: this.frontendContent()
			},
		]); 
	}

	/*
		The venue's own pages come off venue.settings.pages, which isn't known until the venue
		document has been read, so they are served by the two entries at the bottom rather than by a
		route each: '/' for the page marked as the home page, and the wildcard for every other slug.
		CustomPage matches the url back to a page and renders 'page not found' when none does.

		The wildcard cannot swallow anything above: scoreRoute() gives '/*' -1 while a static route
		scores 3 or more, and the router tries them in that order. It does lose to a static route, so
		a slug that collides with one is unreachable - see reservedSlugs in pageSlugs.ts.
	*/
	frontendContent()
	{
		return [
			{
				path: '/menu',
				component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
			},
			{
				path: '/event/:slug',
				component: lazy(() => import('Shared/frontend/eventPage/EventPage')),
			},
			{
				path: '/event/:slug/artist',
				component: lazy(() => import('Shared/frontend/eventPage/ArtistAgreementPage')),
			},
			{
				path: '/',
				component: lazy(() => import('Shared/frontend/CustomPage')),
			},
			{
				path: '*',
				component: lazy(() => import('Shared/frontend/CustomPage')),
			},
		];
	}

	/*
		WARNING
		Careful if leaving a backend page to jump to the frontend or artist GUI. 
		PosterRenderer could still be active and currently this action is NOT accounted for.
	*/
	backend()
	{
		const wrappedRoutes = [
			{
				path: '/',
				component: lazy(() => import('Shared/backend/DashboardPage')),
			},
			{
				path: '/user',
				component: lazy(() => import('Shared/backend/UserPage')),
			},

			/*
				Each group sits under a gate rather than being listed directly, so a feature switched
				off under General on /admin/settings takes its urls with it - the nav entry alone used
				to hide it, and the page still rendered for anyone who typed the url. The gates need
				the venue document, which only exists below BackendWrap, hence the extra level: a
				parent path of '/' contributes no url of its own, so '/admin/events' is still
				'/admin/events'.
			*/
			{
				path: '/',
				component: EventsGate,
				children: this.backendEvent()
			},
			{
				path: '/',
				component: MenusGate,
				children: this.backendMenus()
			},
			{
				path: '/',
				component: SpecialsGate,
				children: this.backendSpecials()
			},
			{
				path: '/',
				component: StaffResourcesGate,
				children: this.backendStaffResources()
			},

			{
				path: [
					'/info',
					'/info/:tab',
				],
				component: lazy(() => import('Shared/backend/infoPage/InfoPage')),
			},
			{
				path: '/posters',
				component: lazy(() => import('Shared/backend/postersPage/PostersPage')),
			},
			{
				path: '/posterTheme/:id',
				component: lazy(() => import('Shared/backend/posterPage/PosterThemePage')),
			},
			{
				path: '/poster/:id',
				component: lazy(() => import('Shared/backend/posterPage/PosterDesignPage')),
			},
			{
				path: '/dashboard',
				component: lazy(() => import('Shared/backend/dashboard/DashboardPage')),
			},
			{
				path: '/nav',
				component: lazy(() => import('Shared/backend/navSettingsPage/NavSettingsPage')),
			},
			{
				path: '/settings',
				component: lazy(() => import('Shared/backend/settingsPage/SettingsPage')),
			},
			{
				path: '/style-builder',
				component: lazy(() => import('Shared/backend/styleBuilderPage/StyleBuilderPage')),
			},
			{
				path: '/testFields',
				component: lazy(() => import('Shared/backend/testFieldsPage/TestFieldsPage')),
			},
			{
				/* Every page built in the page builder, keyed by its slug. */
				path: '/page/:slug',
				component: lazy(() => import('Shared/backend/CustomPage')),
			},
			{
				//TODO return 404
				path: '*',
				component: lazy(() => import('Shared/backend/PageNotFoundPage')),
			}
		];

		return ([
			{
				path: '/login',
				component: lazy(() => import('Shared/users/LoginPage')),
			},
			...sharedRoutes(),
			{
				path: '/admin',
				component: lazy(() => import('Shared/backend/BackendWrap')),
				children: wrappedRoutes
			}
		]); 
	}

	backendStaffResources()
	{
		return [
			{
				path: '/staffResources',
				component: lazy(() => import('Shared/backend/staffResources/StaffResourcesPage')),
			},
			{
				path: '/staffResource/:id',
				component: lazy(() => import('Shared/backend/staffResources/StaffResourcePage')),
			},
		];
	}

	backendSpecials()
	{
		return [
			{
				path: '/specials',
				component: lazy(() => import('Shared/backend/specials/SpecialsPage')),
			},
			{
				path: '/special/:id',
				component: lazy(() => import('Shared/backend/specials/SpecialPage')),
			},
		];
	}

	backendEvent()
	{
		return [
			{
				path: '/events/orphaned-agreements',
				component: lazy(() => import('Shared/backend/eventsPage/OrphanedAgreementsPage')),
			},
			{
				path: '/events',
				component: lazy(() => import('Shared/backend/eventsPage/EventsPage')),
			},
			{
				path: [
					'/event/:slug',
					'/event/:slug/:topTab',
					'/event/:slug/:topTab/:secondTab',
					'/event/:slug/:topTab/:secondTab/:thirdTab'
				],
				component: lazy(() => import('Shared/backend/eventPage/EventPage')),
			}
		];
	}

	backendMenus()
	{
		return [
			{
				path: '/listMenu/:slug',
				component: lazy(() => import('Shared/backend/menu/ListMenuPage')),
			},
			{
				path: '/menus',
				component: lazy(() => import('Shared/backend/menu/MenusPage')),
			},
			{
				path: '/menu-designer',
				component: lazy(() => import('Shared/backend/menu/MenuDesignerPage')),
			},
			{
				path: [
					'/menus/:slug',
					'/menus/:slug/*sectionIds',
				],
				component: lazy(() => import('Shared/backend/menu/SectionPage')),
			},
			{
				path: '/menu-item/:slug/*sectionIds',
				component: lazy(() => import('Shared/backend/menu/MenuItemPage')),
			},
		];
	}
}

export function extractMenuSectionIds(sectionsIds?:string)
{
	if (!sectionsIds)
		return [];

	const parts = [];
	for (const bit of sectionsIds.split('/')) {
		const segments = bit.split('-');
		parts.push({name:segments[0],index:Number(segments[1])});
	}
	return parts;
}

